import { useContext, useEffect, useRef } from 'react';
import { validateFormField } from '../Core/FormFieldValidation';
import { SmartContext } from '../Core/SmartContext';
import { evaluateExpression, getControlValueFromState, handleControlValueChange } from '../Core/SmartFunctions';
import { SimpleFormControlArguments, State } from '../Core/SmartTypes';
import ErrorControl from './ErrorControl';

const toInputValue = (value: any): string => {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const DateSelector = (args: SimpleFormControlArguments) => {
    const { state, dispatch } = useContext(SmartContext);
    const { control, dataKey, parentDataKey } = args;
    const data = getControlValueFromState(dataKey, state as State);
    const formControlRef = useRef<HTMLInputElement>(null);

    const isHidden = evaluateExpression(
        control.hideExpression,
        state?.data,
        getControlValueFromState(parentDataKey as string, state as State)
    );

    const readOnly =
        control.readOnly ||
        evaluateExpression(
            control.readOnlyExpression,
            state?.data,
            getControlValueFromState(parentDataKey as string, state as State)
        );

    useEffect(() => {
        const errorMessages = validateFormField(
            control,
            data,
            state,
            control?.props?.label,
            dataKey,
            getControlValueFromState(parentDataKey as string, state as State)
        );
        dispatch({ type: 'SET_FIELD_VALIDATION_ERRORS', payload: { dataKey, errorMessages } });
    }, []);

    if (isHidden) return <></>;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value || null;
        handleControlValueChange({ control, value, dataKey, parentDataKey, state, dispatch });
    };

    return (
        <>
            <label htmlFor={control.id} className="form-label m-0 mb-1 font-16 font-500 w-100">
                {control.props.label}
            </label>
            <input
                id={control.id}
                data-testid={control.id}
                type="date"
                className="form-control pl-0"
                value={toInputValue(data)}
                required={control.props?.required}
                disabled={readOnly ?? false}
                min={control.props?.min}
                max={control.props?.max}
                onChange={handleChange}
                ref={formControlRef}
            />
            <ErrorControl errorMessages={state?.formValidationErrors[dataKey]} />
        </>
    );
};

export default DateSelector;
